import React, { useState } from "react";
import T from "prop-types";
import styled, { css } from "styled-components/macro";
import Icon from "commons/components/Icon";
import Logo from "commons/components/Logo";
import FadeIn from "commons/components/FadeIn";
import { useTranslation } from "react-i18next";
import { BREAKPOINTS } from "commons/util/breakpoints";

const Section = styled.section`
  display: flex;
  flex-direction: column;
  padding: 32px;

  ${({ isOpposite }) =>
    !isOpposite &&
    css`
      padding-top: 24px;
      background-color: #fbf2ee;
    `}

  @media (max-width: ${BREAKPOINTS.medium}) {
    padding-top: 64px;
    padding-bottom: 72px;
  }
`;

const Heading1 = styled.h1`
  font-size: 16px;
  line-height: 26px;
  font-weight: 400;
  font-family: "Stack Sans Headline", sans-serif;
  margin-bottom: 16px;
  display: flex;
  align-items: center;

  margin-left: -6px;
  gap: 8px;
`;

const Heading2 = styled.h2`
  font-size: 16px;
  line-height: 26px;
  font-weight: 400;
  font-family: "Stack Sans Headline", sans-serif;
  margin-bottom: 16px;
`;

const List = styled.ol`
  font-size: 16px;
  line-height: 28px;
  padding-left: 32px;
  counter-reset: description-list 0;
  font-weight: 300;
`;

const ListItem = styled.li`
  position: relative;

  &:before {
    content: counter(description-list, lower-alpha) ".";
    counter-increment: description-list 1;
    color: var(--brown-160);
    position: absolute;
    left: -28px;

    ${({ isOpposite }) =>
      isOpposite &&
      css`
        content: "✕";
      `}
  }
`;

const NextDescriptionButton = styled.button`
  background-color: var(--brown-190);
  border-radius: var(--border-radius-1);
  padding: 4px;

  &:hover {
    background-color: var(--brown-180);
  }
`;

function DescriptionList({ className, descriptions, isOpposite }) {
  const { t } = useTranslation();
  const [descriptionLimit, setDescriptionLimit] = useState(2);

  function raiseDescriptionLimit() {
    setDescriptionLimit(currentLimit => currentLimit + 1);
    if (descriptionLimit < descriptions.length - 1) {
      window.scrollBy({ top: 28, behavior: "auto" });
    }
  }

  return (
    <Section isOpposite={isOpposite}>
      {isOpposite ? (
        <Heading2>
          <Logo withGoose={false} /> is <em>not</em>:
        </Heading2>
      ) : (
        <Heading1>
          <Logo /> is:
        </Heading1>
      )}
      <List>
        {descriptions.slice(0, descriptionLimit).map((description, i) => (
          <FadeIn key={i}>
            <ListItem isOpposite={isOpposite}>{description.label}</ListItem>
          </FadeIn>
        ))}
        {descriptionLimit < descriptions.length && (
          <ListItem isOpposite={isOpposite}>
            <NextDescriptionButton
              onClick={raiseDescriptionLimit}
              data-umami-event={isOpposite ? "Expand opposite list" : "Expand list"}
            >
              <Icon name="add" size={20} />
            </NextDescriptionButton>
          </ListItem>
        )}
      </List>
    </Section>
  );
}

DescriptionList.propTypes = {
  className: T.string,
  descriptions: T.array,
  isOpposite: T.bool,
};

export default DescriptionList;
